import { contrast, hexToRgb, readableOn } from '../lib/color';
import type { Border, Decoration, Design, Pattern } from '../storage/schemas';
import { EXTRA_SITE_PALETTES, REAL_DESIGNS, REAL_SECTIONS } from './realDesigns';

export const DEFAULT_DESIGN_ID = 'd000';

// ---------------------------------------------------------------------------
// Reference collection: the sections, order and #dXXX numbering of the reference app's design gallery
// (from user-supplied screenshots), with colours snapped to values published on the reference website.
// ---------------------------------------------------------------------------

const PATTERN_LABEL: Record<Pattern, string> = { grid: 'Grid', ruled: 'Lined', dots: 'Dots', plain: 'Plain' };

function hueName(hex: string): string {
  const [r, g, b] = hexToRgb(hex).map((v) => v / 255);
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  const d = max - min;
  const s = d === 0 ? 0 : d / (1 - Math.abs(2 * l - 1));
  if (l > 0.95 && s < 0.5) return 'White';
  if (l < 0.13) return 'Ink';
  if (s < 0.12) return l > 0.75 ? 'Fog' : l > 0.4 ? 'Stone' : 'Charcoal';
  let h = 0;
  if (d !== 0) {
    if (max === r) h = ((g - b) / d) % 6;
    else if (max === g) h = (b - r) / d + 2;
    else h = (r - g) / d + 4;
    h *= 60;
    if (h < 0) h += 360;
  }
  const dark = l < 0.32;
  const pale = l > 0.82;
  const names: [number, string, string, string][] = [
    [12, 'Tomato', 'Rosewood', 'Blush'],
    [32, 'Tangerine', 'Cocoa', 'Peach'],
    [46, 'Honey', 'Walnut', 'Cream'],
    [64, 'Lemon', 'Olive', 'Butter'],
    [90, 'Lime', 'Moss', 'Pistachio'],
    [150, 'Leaf', 'Forest', 'Mint'],
    [185, 'Teal', 'Deep Teal', 'Seafoam'],
    [215, 'Sky', 'Navy', 'Ice'],
    [250, 'Blue', 'Midnight', 'Periwinkle'],
    [285, 'Violet', 'Plum', 'Lavender'],
    [330, 'Pink', 'Wine', 'Rose'],
    [361, 'Red', 'Maroon', 'Blush'],
  ];
  const row = names.find(([max]) => h < max) ?? names[names.length - 1];
  return dark ? row[2] : pale ? row[3] : row[1];
}

function referenceInk(paper: string, accent: string): string {
  // The reference tints text with the accent colour. Keep that look unless it becomes illegible.
  return contrast(paper, accent) >= 1.8 ? accent : readableOn(paper, accent, 4.5);
}

/** Section headings and descriptions (descriptions are MemoNest's own wording). */
export const SECTION_INFO: Record<string, string> = {
  Original: 'The classic sticky-note look',
  Clear: 'See-through notes that let your wallpaper show',
  Colorful: 'Bright, soft colours that stay easy to read',
  Retro: 'Muted, nostalgic paper tones',
  'Sticky Notes': 'Bold sticky-note colours for things you must not miss',
  Cute: 'Gentle pastels for a cute lock screen',
  Dark: 'Calm dark notes that are easy on the eyes',
  Simple: 'Clean paper with nothing extra',
  'Calm Blue': 'Quiet greys, greens and blues',
  'More classics': 'More palettes from the original collection',
};

const sectionOf = new Map<string, string>(REAL_SECTIONS.flatMap((s) => s.ids.map((id) => [id, s.title] as [string, string])));

const realDesigns: Design[] = REAL_DESIGNS.map(([id, paper, pattern, line, ink, accent]) => ({
  id,
  name: `#${id}`,
  category: sectionOf.get(id) ?? 'Original',
  source: 'reference',
  paper,
  line,
  ink,
  accent,
  pattern,
  patternScale: 1,
  font: 'schoolbell',
}));

// Clear designs are translucent; their exact opacities are estimated from the gallery's checkerboard previews.
const clearDesigns: Design[] = (
  [
    ['d100', '#FFFFFF', 0.14, 'plain', '#FFFFFF', 0],
    ['d101', '#000000', 0.22, 'plain', '#FFFFFF', 0],
    ['d102', '#000000', 0.5, 'grid', '#FFFFFF', 0.1],
    ['d103', '#FFFFFF', 0.24, 'grid', '#FFFFFF', 0.28],
  ] as const
).map(([id, paper, alpha, pattern, line, lineAlpha]) => ({
  id,
  name: `#${id}`,
  category: 'Clear',
  source: 'reference' as const,
  paper,
  paperAlpha: alpha,
  line,
  lineAlpha: lineAlpha || undefined,
  ink: '#FFFFFF',
  accent: '#FFFFFF',
  pattern,
  patternScale: 1,
  font: 'schoolbell',
}));

const moreClassics: Design[] = EXTRA_SITE_PALETTES.map(([paper, pattern, line, accent], i) => ({
  id: `d2${String(i).padStart(2, '0')}`,
  name: `${hueName(paper)} ${PATTERN_LABEL[pattern]}`,
  category: 'More classics',
  source: 'reference',
  paper,
  line,
  ink: referenceInk(paper, accent),
  accent,
  pattern,
  patternScale: 1,
  font: 'schoolbell',
}));

const referenceDesigns: Design[] = [...realDesigns, ...clearDesigns, ...moreClassics];

/** Old development ids (ref-NN) still resolve to the default. */
export const LEGACY_DESIGN_IDS: Record<string, string> = { 'ref-01': 'd000' };

// ---------------------------------------------------------------------------
// MemoNest original collection
// ---------------------------------------------------------------------------

type Row = [name: string, paper: string, line: string, ink: string, pattern: Pattern, font: string, decoration?: Decoration, border?: Border, patternScale?: number];

const solid = (color: string, width = 0.008): Border => ({ style: 'solid', color, width });
const dashed = (color: string, width = 0.006): Border => ({ style: 'dashed', color, width });
const stitch = (color: string): Border => ({ style: 'stitch', color, width: 0.005 });
const double = (color: string): Border => ({ style: 'double', color, width: 0.012 });

const ORIGINALS: Record<string, Row[]> = {
  Minimal: [
    ['Paper White', '#FFFFFF', '#EFEFEF', '#1C1C1E', 'plain', 'inter'],
    ['Soft Grey', '#F2F2F0', '#E2E2DE', '#2B2B2B', 'dots', 'nunito'],
    ['Linen', '#F7F2E9', '#E8DFCF', '#3A3226', 'ruled', 'lora'],
    ['Graphite', '#2A2A2C', '#3A3A3D', '#EDEDED', 'plain', 'inter'],
    ['Bone', '#EFEADF', '#E0D9CA', '#4A4235', 'grid', 'patrick-hand', undefined, undefined, 1.5],
    ['Snow Dots', '#FBFBFB', '#DADADA', '#333333', 'dots', 'kalam', undefined, undefined, 1.5],
    ['Mist', '#E6ECEF', '#D3DCE1', '#33434C', 'ruled', 'nunito'],
    ['Oat', '#E9DFCB', '#DCD0B8', '#5A4A2F', 'plain', 'kalam'],
    ['Clean Slate', '#DDE3E8', '#C9D2DA', '#1E2B36', 'grid', 'inter', undefined, undefined, 2],
    ['Ivory Lines', '#FFFDF6', '#ECE6D2', '#2F2A20', 'ruled', 'noto-serif'],
    ['Pebble', '#C9C6C0', '#BAB7B0', '#2E2C29', 'dots', 'short-stack'],
    ['Night Plain', '#121214', '#1E1E22', '#C9C9D1', 'plain', 'nunito'],
  ],
  Pastel: [
    ['Cotton Candy', '#FFD6E8', '#F7C1D9', '#A1376B', 'grid', 'indie-flower'],
    ['Baby Blue', '#D4EBFF', '#BEDDF9', '#2F5E8C', 'dots', 'patrick-hand'],
    ['Pistachio Cream', '#E3F5D3', '#CFE8BB', '#3E6B26', 'ruled', 'kalam'],
    ['Lilac Mist', '#E9DDFB', '#D9C8F5', '#5B3E8F', 'grid', 'gloria'],
    ['Peach Sorbet', '#FFE1CC', '#F8CDAE', '#A0522D', 'dots', 'caveat'],
    ['Lemon Chiffon', '#FFF6C7', '#F2E6A6', '#7A6400', 'grid', 'schoolbell'],
    ['Seafoam', '#D2F4EA', '#B9E8DA', '#22705A', 'ruled', 'shadows'],
    ['Apricot', '#FFE8D1', '#F5D2AE', '#9B5A1E', 'grid', 'mynerve'],
    ['Powder Pink', '#F9E1E5', '#EFCBD2', '#8E3B4C', 'plain', 'dancing'],
    ['Periwinkle', '#DCE2FF', '#C6CFF7', '#3C47A0', 'dots', 'coming-soon'],
    ['Butter Mint', '#EAF7E1', '#D5EBC6', '#4D7A35', 'grid', 'architects'],
    ['Sakura Milk', '#FDECF1', '#F6D3DE', '#B04A72', 'ruled', 'yomogi'],
    ['Pale Sky', '#E8F4FB', '#D0E6F2', '#2F6B8E', 'plain', 'gaegu'],
    ['Lavender Grid', '#EEE8F7', '#DCD2EE', '#4F3A79', 'grid', 'klee'],
  ],
  Night: [
    ['Chalkboard', '#2E4237', '#3A5145', '#F2F2E6', 'plain', 'shadows', 'none', solid('#5A3F2A', 0.02)],
    ['Midnight Ink', '#101826', '#1C2738', '#9EC5FF', 'grid', 'patrick-hand'],
    ['Espresso', '#2B1D16', '#3A2A21', '#E9C9A6', 'ruled', 'caveat'],
    ['Neon Night', '#0E0E12', '#1D1D26', '#39FF9C', 'dots', 'vt323'],
    ['Plum Dark', '#2A1730', '#3A2342', '#F3B8FF', 'grid', 'gloria'],
    ['Deep Ocean', '#0B2A3A', '#123A4E', '#7FE3F0', 'ruled', 'kalam'],
    ['Charcoal Gold', '#1F1F1F', '#2E2E2E', '#E8C547', 'plain', 'playfair'],
    ['Forest Night', '#14261C', '#1E3528', '#B6E3B0', 'grid', 'mynerve'],
    ['Wine', '#3A1018', '#4C1A24', '#FFC2CB', 'dots', 'dancing'],
    ['Slate Blue', '#262B3F', '#333954', '#C6CCF5', 'grid', 'nunito'],
    ['Cocoa Dots', '#33241E', '#45332B', '#F6D7B0', 'dots', 'schoolbell'],
    ['Blackout', '#000000', '#161616', '#FFFFFF', 'plain', 'inter'],
  ],
  Study: [
    ['Notebook', '#FFFFFF', '#BFD8F2', '#1F2D4A', 'ruled', 'patrick-hand', 'margin'],
    ['Graph Paper', '#F7FBFF', '#B8D7F0', '#1D3B5C', 'grid', 'architects', undefined, undefined, 0.5],
    ['Engineering Pad', '#EDF5E1', '#B9D69A', '#2E4A1F', 'grid', 'space-mono', undefined, undefined, 0.75],
    ['Cornell', '#FFFFFF', '#D9E4EC', '#22313F', 'ruled', 'kalam', 'margin'],
    ['Flashcard', '#FFFFFF', '#F2B8B8', '#222222', 'ruled', 'inter', 'none', solid('#E0E0E0', 0.006)],
    ['Exam Cram', '#FFF8D6', '#EADFA6', '#6B2B00', 'grid', 'marker'],
    ['Vocabulary', '#F4F0FF', '#DCD3F7', '#3B2A7A', 'ruled', 'noto-serif', 'margin'],
    ['Kanji Practice', '#FFFDF7', '#EBC9C2', '#2A1A14', 'grid', 'klee', undefined, undefined, 2],
    ['Hangul Notes', '#F7FAFF', '#D3DEF2', '#1F2F57', 'ruled', 'nanum-pen'],
    ['Formula Sheet', '#F2F4F5', '#CDD5DA', '#1A252C', 'grid', 'space-mono'],
    ['Binder Page', '#FFFFFF', '#CFE2F3', '#283548', 'ruled', 'short-stack', 'binder'],
    ['Highlighter', '#FFFFA8', '#F0EC7C', '#2B2B00', 'ruled', 'marker'],
  ],
  Productivity: [
    ['Today', '#FFFFFF', '#E8E8E8', '#111111', 'dots', 'inter', 'none', solid('#111111', 0.006)],
    ['Focus Red', '#FF5A4E', '#F2493D', '#FFFFFF', 'plain', 'inter'],
    ['Deep Work', '#1B2A41', '#24364F', '#F5F7FA', 'dots', 'nunito'],
    ['Kanban Yellow', '#FFE24A', '#F2D235', '#2A2400', 'grid', 'nunito'],
    ['Inbox Zero', '#E8F1FF', '#D2E2FA', '#0B3A82', 'ruled', 'inter'],
    ['Errands', '#D9F2E3', '#BEE6CF', '#155C34', 'grid', 'patrick-hand'],
    ['Priority', '#FFF0E6', '#FCD9C2', '#B23A00', 'dots', 'nunito', 'none', solid('#FF7A2F', 0.01)],
    ['Meeting', '#EEF0F3', '#DADEE4', '#2C3440', 'ruled', 'inter'],
    ['Habit Tracker', '#F3FAEC', '#D9EDC6', '#2D5A12', 'grid', 'short-stack', undefined, undefined, 1.5],
    ['Daily Plan', '#FFFDF2', '#EFE8C8', '#3D3520', 'ruled', 'kalam', 'margin'],
    ['Sprint', '#E3E7FF', '#CBD2FB', '#2A2E8C', 'grid', 'space-mono'],
    ['Done List', '#E9FBEF', '#C8F0D5', '#0D7A3A', 'dots', 'patrick-hand'],
  ],
  Colourful: [
    ['Electric Blue', '#1F6BFF', '#1A5FE6', '#FFFFFF', 'grid', 'marker'],
    ['Hot Pink', '#FF3EA5', '#F02A94', '#FFFFFF', 'dots', 'gloria'],
    ['Lime Pop', '#B6F500', '#A6E200', '#1F3300', 'grid', 'marker'],
    ['Sunset', '#FF7A3D', '#F06A2C', '#3A1200', 'ruled', 'caveat'],
    ['Grape', '#7B2FF7', '#6C22E6', '#F3E9FF', 'grid', 'patrick-hand'],
    ['Aqua', '#00C2D1', '#00B0BE', '#003B40', 'dots', 'kalam'],
    ['Tomato', '#FF4B3E', '#EE3B2F', '#FFF1EF', 'grid', 'schoolbell'],
    ['Marigold', '#FFB000', '#F0A200', '#3D2A00', 'plain', 'marker'],
    ['Fuchsia', '#D6249F', '#C21A8E', '#FFE6F6', 'ruled', 'dancing'],
    ['Emerald', '#00A86B', '#00975F', '#E6FFF4', 'grid', 'mynerve'],
    ['Cobalt Dots', '#2541B2', '#2F4CC2', '#FFD84D', 'dots', 'shadows'],
    ['Coral Reef', '#FF6F61', '#F2635A', '#FFFFFF', 'plain', 'indie-flower'],
    ['Mango', '#FFC93C', '#F0BA2C', '#5A2D00', 'grid', 'gloria'],
    ['Ultraviolet', '#4B0082', '#5A1194', '#FFD6FF', 'grid', 'tiny5'],
  ],
  Seasonal: [
    ['Cherry Blossom', '#FFE4EC', '#F8CEDB', '#9C2F55', 'plain', 'yomogi', 'sparkle'],
    ['Spring Meadow', '#E7F7D4', '#D1EBB6', '#2F5E1A', 'grid', 'kalam', 'leaves'],
    ['Summer Sun', '#FFF1A8', '#F5E487', '#A44A00', 'dots', 'gloria', 'sun'],
    ['Beach Day', '#BDEBFF', '#A4DDF5', '#0B4E6E', 'ruled', 'caveat', 'sun'],
    ['Autumn Leaves', '#F6D8B8', '#EAC39C', '#7A2E0E', 'grid', 'architects', 'leaves'],
    ['Pumpkin Spice', '#E57A2E', '#D86C22', '#2B1300', 'dots', 'marker'],
    ['Halloween', '#1C1420', '#2A1F30', '#FF8A1F', 'grid', 'gloria', 'stars'],
    ['First Snow', '#EEF5FB', '#DBE8F3', '#2B4C6F', 'plain', 'patrick-hand', 'snow'],
    ['Holly', '#B71C2C', '#A51626', '#F4F0E0', 'grid', 'dancing', 'snow'],
    ['Evergreen', '#1F4D35', '#285C41', '#F7E9C4', 'dots', 'mynerve', 'snow'],
    ['Valentine', '#FFD1DC', '#F7BCCB', '#C2185B', 'grid', 'pacifico', 'hearts'],
    ['New Year Gold', '#141414', '#232323', '#F2C94C', 'plain', 'playfair', 'sparkle'],
    ['Lunar Red', '#C62828', '#B71C1C', '#FFD54F', 'dots', 'klee', 'stars'],
    ['Rainy Day', '#C9D6DF', '#B5C5D0', '#29404F', 'ruled', 'shadows'],
    ['Picnic', '#FFF7EC', '#F4B3B3', '#8C1F1F', 'grid', 'short-stack', 'tape', undefined, 2],
    ['Harvest Moon', '#2C2A4A', '#383559', '#FFE08A', 'dots', 'caveat', 'stars'],
  ],
  Typography: [
    ['Typewriter', '#F4EEDC', '#E6DDC4', '#2B2620', 'plain', 'space-mono'],
    ['Arcade', '#1A1446', '#251D5E', '#FFE600', 'grid', 'press-start'],
    ['Terminal', '#0B0F0B', '#122012', '#33FF66', 'plain', 'vt323'],
    ['Editorial', '#FAF7F2', '#ECE6DA', '#1B1B1B', 'ruled', 'playfair'],
    ['Love Letter', '#FFF5F7', '#F5DDE3', '#7A1F3D', 'plain', 'homemade-apple'],
    ['Calligraphy', '#F7F1E3', '#EADFC6', '#3B2A14', 'ruled', 'dancing'],
    ['Pixel Note', '#DFF5E1', '#C5E8C9', '#14532D', 'grid', 'tiny5'],
    ['Dot Matrix', '#F2F2F2', '#DDDDDD', '#222222', 'dots', 'dotgothic'],
    ['Signpainter', '#FFE9C7', '#F5D6A8', '#8A1C00', 'plain', 'pacifico'],
    ['Book Page', '#F3ECDD', '#E5DBC6', '#2E2416', 'plain', 'lora'],
    ['Marker Board', '#FFFFFF', '#F0F0F0', '#1946B8', 'plain', 'marker', 'none', solid('#B8BEC6', 0.014)],
    ['Postcard', '#FBF4E4', '#E9D9B9', '#284B63', 'ruled', 'nothing', 'none', double('#284B63')],
  ],
  Decorative: [
    ['Washi Tape', '#FFF8E7', '#F0E2C0', '#5B4220', 'grid', 'kalam', 'tape'],
    ['Pinned Memo', '#FFF3A3', '#F2E37E', '#3D3200', 'plain', 'schoolbell', 'pin'],
    ['Stitched', '#D8E8F5', '#C2D8EA', '#1D3B57', 'dots', 'patrick-hand', 'none', stitch('#5A7FA3')],
    ['Dog-ear', '#FFFFFF', '#E9E9E9', '#2A2A2A', 'ruled', 'indie-flower', 'corner'],
    ['Starry', '#1E2246', '#2A2F5C', '#FFE9A8', 'plain', 'gloria', 'stars'],
    ['Heart Border', '#FFE8EE', '#F7D0DB', '#B0244E', 'plain', 'pacifico', 'hearts', dashed('#E46A8D')],
    ['Gold Frame', '#FFFBF0', '#F1E6C8', '#5A4300', 'plain', 'playfair', 'none', double('#C9A227')],
    ['Kraft Tag', '#C9A27A', '#B88F66', '#2E1B0C', 'plain', 'marker', 'pin', stitch('#7A5534')],
    ['Ticket', '#FFE7A1', '#F2D47E', '#6A2400', 'dots', 'space-mono', 'none', dashed('#6A2400', 0.008)],
    ['Sparkle Note', '#F3E8FF', '#E3D0FB', '#5A2A8A', 'grid', 'caveat', 'sparkle'],
    ['Garden Frame', '#F1F8E8', '#DCEBCB', '#2F5D1E', 'ruled', 'kalam', 'leaves', solid('#7FB069', 0.01)],
    ['Polaroid', '#FFFFFF', '#F2F2F2', '#222222', 'plain', 'reenie', 'tape', solid('#E6E6E6', 0.03)],
  ],
};

const originalDesigns: Design[] = Object.entries(ORIGINALS).flatMap(([category, rows]) =>
  rows.map(([name, paper, line, ink, pattern, font, decoration, border, patternScale]) => ({
    id: `mn-${name.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`,
    name,
    category,
    source: 'original' as const,
    paper,
    line,
    ink,
    accent: ink,
    pattern,
    patternScale: patternScale ?? 1,
    font,
    decoration,
    border,
  })),
);

export const BUILT_IN_DESIGNS: Design[] = [...referenceDesigns, ...originalDesigns];

/** Gallery sections in display order: the reference app's sections first, then MemoNest originals. */
export const REFERENCE_SECTIONS = [...REAL_SECTIONS.map((s) => s.title), 'More classics'];
export const ORIGINAL_SECTIONS = Object.keys(ORIGINALS);
export const DESIGN_CATEGORIES = [...REFERENCE_SECTIONS, ...ORIGINAL_SECTIONS];

/** Designs of one section, in the reference order. */
export function designsInSection(section: string): Design[] {
  const real = REAL_SECTIONS.find((s) => s.title === section);
  if (real) return real.ids.map((id) => byId.get(id)).filter((d): d is Design => !!d);
  return BUILT_IN_DESIGNS.filter((d) => d.category === section);
}

const byId = new Map(BUILT_IN_DESIGNS.map((d) => [d.id, d]));

export function getBuiltInDesign(id: string): Design | undefined {
  return byId.get(LEGACY_DESIGN_IDS[id] ?? id);
}
